import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, catchError, filter, forkJoin, from, map, Observable, of, shareReplay, switchMap, tap, throwError } from "rxjs";
import { inject, Injectable, makeStateKey, TransferState } from "@angular/core";
import { Address, AddressRequest, Amenity, CreatePropertyImageRequest, CreatePropertyRequest, PagedResponse, Property, PropertyAmenity, PropertyAmenityBulkRequest, PropertyCalendar, PropertyCalendarRequest, PropertyImage, PropertySearchRequest, PropertyStats, PropertySummaryDto, Timezone, UserProperty } from "../models/property.model";
import { User } from "../../../shared/user/models/user.model";
import { FeaturedPropertyDto } from "../components/featured-globe/featured-globe.component";

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  private http = inject(HttpClient);

  private transferState = inject(TransferState);
  private apiUrl = environment.apiUrl; // Base URL from environment config
  private cache = new Map<string, BehaviorSubject<any>>();

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      // Add other headers as needed, e.g., Authorization
      // 'Authorization': `Bearer ${yourToken}`
    });
  }

  getPropertyById = (id: string): Observable<Property> => {
    const PROPERTY_KEY = makeStateKey<Property>(`property-details-${id}`);

    // If the data is already in the state (on the client), use it and clear it
    if (this.transferState.hasKey(PROPERTY_KEY)) {
      const savedProperty = this.transferState.get(PROPERTY_KEY, null as any);
      this.transferState.remove(PROPERTY_KEY);
      return of(savedProperty);
    }

    // Otherwise, fetch it from the API and save it to the state
    return this.http.get<Property>(`${this.apiUrl}/properties/${id}`, { headers: this.getHeaders() })
      .pipe(
        tap(property => {
          // Only set the state if we are currently on the server
          this.transferState.set(PROPERTY_KEY, property);
          //console.log('Property fetched and stored in TransferState:', property);
        }),
        catchError(this.handleError)
      );
  };
  
  get(): Observable<Property[]> {
    return this.http.get<Property[]>(`${this.apiUrl}/properties`, { headers: this.getHeaders() });
  }

  getAll(): Observable<PropertySummaryDto[]> {
    return this.http.get<PropertySummaryDto[]>(`${this.apiUrl}/properties/all`, { headers: this.getHeaders() });
  }

  getPropertyStream(id: string): Observable<any> {
    if (!this.cache.has(id)) {
      const subject = new BehaviorSubject<any>(null);
      this.cache.set(id, subject);

      this.loadEnriched(id).subscribe({
        next: enriched => {
          if (enriched !== null) {
            subject.next(enriched);
          }
        },
        error: () => subject.next(null)
      });
    }

    return this.cache.get(id)!.asObservable().pipe(
      filter(property => property !== null), // ← NEVER emit null
      shareReplay(1)
    );
  }
  
  searchProperties = (request: PropertySearchRequest): Observable<PagedResponse<Property>> =>
    this.http.post<PagedResponse<Property>>(`${this.apiUrl}/properties/search`, request);

  // getPropertyById = (id: string) => 
  //   this.http.get<Property>(`${this.apiUrl}/properties/${id}`, { headers: this.getHeaders() })
  //     .pipe(tap(p => console.log('Raw property from server:', p)));

  createAddress = (body: AddressRequest): Observable<Address> => 
    this.http.post<Address>(`${this.apiUrl}/addresses`, body, { headers: this.getHeaders() });

  addressExists = (body: AddressRequest): Observable<boolean> => 
    this.http.post<boolean>(`${this.apiUrl}/addresses/exists`, body, { headers: this.getHeaders() });

  getPropertyOwnerByPropertyId = (propertyId: string): Observable<User> =>
    this.http.get<User>(`${this.apiUrl}/properties/${propertyId}/owner`, { headers: this.getHeaders() });

  updateAddress(propertyId: string, newAddress: Address): Observable<Property> {
    const cacheSubject = this.cache.get(propertyId);
    if (!cacheSubject?.value) {
      throw new Error('Property not loaded in cache');
    }

    const currentProperty = cacheSubject.value as Property;
    const optimistic: Property = {
      ...currentProperty,
      address: {
        ...currentProperty.address,
        ...newAddress,
        country: newAddress.country ?? 'USA'
      }
    };
    cacheSubject.next(optimistic);

    return this.http.put<Address>(
      `${this.apiUrl}/addresses/${newAddress.id}`,
      newAddress,
      { headers: this.getHeaders() }
    ).pipe(
      switchMap(() => this.loadEnriched(propertyId)),
      tap((enriched: any) => {
        const subject = this.cache.get(propertyId);
        if (subject) {
          subject.next(enriched);
        }
      }),
      catchError(err => {
        cacheSubject.next(currentProperty);
        return throwError(() => err);
      })
    );
  }

  getTimezones = (): Observable<Timezone[]> =>
    this.http.get<Timezone[]>(`${this.apiUrl}/properties/timezones`, { headers: this.getHeaders() });

  createProperty = (body: CreatePropertyRequest): Observable<Property> => 
    this.http.post<Property>(`${this.apiUrl}/properties`, body, { headers: this.getHeaders() });

  getActivePropertyStates = () => 
    this.http.get<string[]>(`${this.apiUrl}/properties/active-states`, { headers: this.getHeaders() });

  createPropertyImage = (propertyId: string, body: CreatePropertyImageRequest): Observable<PropertyImage> => 
    this.http.post<PropertyImage>(`${this.apiUrl}/properties/${propertyId}/images/base64`, body, { headers: this.getHeaders() });

  getPropertyImages = (propertyId: string): Observable<PropertyImage[]> =>
    this.http.get<PropertyImage[]>(`${this.apiUrl}/properties/${propertyId}/images`, { headers: this.getHeaders() })
      .pipe(
        map(images => {
          // Check if *any* image has sortOrder (and it's a number)
          const hasSortOrder = images.some(img => typeof img.sortOrder === 'number');

          if (hasSortOrder) {
            return [...images].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
          }

          return images; // No sorting if sortOrder doesn't exist
        })
      );

  updatePropertyImages = (propertyId: string, body: PropertyImage[]): Observable<PropertyImage[]> =>
    this.http.put<PropertyImage[]>(`${this.apiUrl}/properties/${propertyId}/images`, body, { headers: this.getHeaders() });

  getPropertyLikes = (userId: string) => this.http.get<Property[]>(`${this.apiUrl}/properties/user/${userId}/likes`, { headers: this.getHeaders() });

  getAmenities = () => this.http.get<Amenity[]>(`${this.apiUrl}/amenities`, { headers: this.getHeaders() });

  setPropertyAmenities = (request: PropertyAmenityBulkRequest): Observable<PropertyAmenity> => 
    this.http.post<PropertyAmenity>(`${this.apiUrl}/property-amenity/bulk`, request, { headers: this.getHeaders() });

  getPropertyAmenities = (propertyId: string): Observable<PropertyAmenity[]> =>
    this.http.get<PropertyAmenity[]>(`${this.apiUrl}/property-amenity/${propertyId}`);

  setPropertyCalendars = (propertyId: string, propertyCalendars: PropertyCalendarRequest[]) =>
    this.http.post(`${this.apiUrl}/property-calendar/${propertyId}/list`, propertyCalendars, { headers: this.getHeaders() });  

  getPropertyCalendars = (propertyId: string): Observable<PropertyCalendar[]> =>
    this.http.get<PropertyCalendar[]>(`${this.apiUrl}/property-calendar/${propertyId}`, { headers: this.getHeaders() });

  getPropertyStats = (): Observable<PropertyStats> => this.http.get<PropertyStats>(`${this.apiUrl}/properties/stats`, { headers: this.getHeaders() });

  private loadEnriched(id: string): Observable<any> {
    return this.getPropertyById(id).pipe(
      switchMap((property: Property | null) => {
        if (!property) return of(null);

        return forkJoin({
          propertyAmenities: this.getPropertyAmenities(id),
          allAmenities: this.getAmenities(),
          images: this.getPropertyImages(id),
          calendars: this.getPropertyCalendars(id)
        }).pipe(
          map(({ propertyAmenities, allAmenities, images, calendars }) => {
            const safePA = Array.isArray(propertyAmenities) ? propertyAmenities : [];
            const safeAA = Array.isArray(allAmenities) ? allAmenities : [];
            const safeImg = Array.isArray(images) ? images : [];
            const safeCal = Array.isArray(calendars) ? calendars : [];

            const amenityIds = safePA
              .filter(pa => pa?.propertyId === id)
              .map(pa => pa.amenityId)
              .filter(Boolean);

            const matched = safeAA.filter(a => a?.id && amenityIds.includes(a.id));

            return {
              ...property,
              amenities: matched,
              images: safeImg,
              calendars: safeCal
            };
          }),
          catchError(err => {
            console.error('Enrich error:', err);
            return of({ ...property, amenities: [], images: [], calendars: [] });
          })
        );
      }),
      catchError(err => {
        console.error('Load property error:', err);
        return of(null);
      })
    );
  }
  
  updateProperty(id: string, updates: Partial<Property>): Observable<Property> {
    //console.log('updateProperty', id, updates);
    return this.http.patch<Property>(
      `${this.apiUrl}/properties/${id}/fields`,
      updates,
      { headers: this.getHeaders() }
    ).pipe(
      tap((updatedProperty) => {
        //console.log('Raw updated property:', updatedProperty);

        const subject = this.cache.get(id);
        if (!subject?.value) return;

        const cleanUpdates: Partial<Property> = {};

        Object.entries(updatedProperty).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            cleanUpdates[key as keyof Property] = value;
          }
        });

        if (Object.keys(cleanUpdates).length === 0) return;

        const merged = { ...subject.value, ...cleanUpdates };
        //console.log('Merged into cache:', merged);
        subject.next(merged);
      }),
      catchError(err => {
        console.error('Update failed:', err);
        throw err;
      })
    );
  }

  refresh(id: string) {
    this.loadEnriched(id).subscribe(enriched => this.cache.get(id)?.next(enriched));
  }

  setUserProperty = (propertyId: string, userId: string): Observable<UserProperty> => 
    this.http.post<UserProperty>(`${this.apiUrl}/user-property`, { propertyId: propertyId, userId: userId}, { headers: this.getHeaders() });

  getUserProperties = (userId: string): Observable<UserProperty[]> => 
    this.http.get<UserProperty[]>(`${this.apiUrl}/user-property/${userId}`, { headers: this.getHeaders() });

  getLatLong(_address: Address): Observable<[number, number]> {
    const street = _address.street || '';
    const city = _address.city || '';
    const state = _address.state || '';
    const zipCode = _address.zipCode || '';
    
    const address = `${street}, ${city}, ${state} ${zipCode}`.trim();
    if (!address) throw new Error('Missing address');
    //console.log('address to get', address);
    
    const promise = new Promise<[number, number]>((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          resolve([lat, lng]);
        } else {
          reject(new Error(`Invalid address: ${status}`));
        }
      });
    });

    return from(promise);
  }

  getFeaturedItems() {
    return this.http.get<FeaturedPropertyDto[]>(`${this.apiUrl}/items/featured`, { headers: this.getHeaders() });
  }

  // Error handling
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server error: ${error.status} - ${error.message}`;
    }

    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }


}